import { ICustomer, ICustomerField } from "../../models/customer.ts";
import { ICustomerCsvFormat } from "../../models/customerCsvFormat.ts";

// 日本の番号(+81で始まる)のみ変換する関数
const formatPhoneNumber = (phoneStr: string) => {
  if (phoneStr.startsWith("+81")) {
    const localNumber = phoneStr.slice(3);
    return "0" + localNumber;
  }

  // その他の国番号はそのまま文字列で返す
  return phoneStr;
};

const formatRole = (role: string): string => {
  switch (role) {
    case "end-user":
      return "エンドユーザー";
    case "agent":
      return "エージェント";
    case "admin":
      return "エージェント";
    default:
      return role;
  }
};

export const transformFromCustomerToCsv = (
  user: ICustomer,
  orgMap: Map<number, string>,
  customRoleMap: Map<number, string>,
  activeFields: ICustomerField[],
): ICustomerCsvFormat => {
  const csv: ICustomerCsvFormat = {
    name: user.name ?? "",
    emails: user.email ?? "",
    ticket_restriction: user.ticket_restriction ?? "",
    external_id: user.external_id ?? "",
    details: user.details ?? "",
    notes: user.notes ?? "",
    phones: !user.shared_phone_number && user.phone ? '="' + formatPhoneNumber(user.phone) + '"' : "",
    shared_phone: user.shared_phone_number && user.phone ? '="' + formatPhoneNumber(user.phone) + '"' : "",
    role: formatRole(user.role),
    custom_role:
      user.custom_role_id
        ? (customRoleMap.get(user.custom_role_id) ?? "")
        : "",
    alias: user.alias ?? "",
    time_zone: user.time_zone ?? "",
    locale: user.locale ?? "",
    locale_id: user.locale_id ?? "",
    moderator: user.moderator ? "true" : "false",
    only_private_comments: user.only_private_comments ? "true" : "false",
    remote_photo_url: user.photo?.content_url ?? "",
    signature: user.signature ?? "",
    suspended: user.suspended ? "true" : "false",
    skip_verify_email: "true",
    organizations:
      user.organization_id
        ? (orgMap.get(user.organization_id) ?? "")
        : "",
    default_group_id: user.default_group_id ?? "",
    tags: user.tags ? user.tags.join(",") : "",
  };

  for (const field of activeFields) {
    const value = user.user_fields?.[field.key];
    const headerKey = `custom_fields.${field.key}`;

    if (value === null || value === undefined) {
      csv[headerKey] = "";
    } else if (field.type === "checkbox") {
      csv[headerKey] = value ? "true" : "false";
    } else {
      csv[headerKey] = value;
    }
  }

  csv["_ref_id"] = user.id;
  csv["_ref_url"] = user.url ?? "";
  csv["_ref_role_type"] = user.role_type ?? "";
  csv["_ref_restricted_agent"] = user.restricted_agent ? "true" : "false";
  csv["_ref_iana_time_zone"] = user.iana_time_zone ?? "";
  csv["_ref_active"] = user.active ? "true" : "false";
  csv["_ref_verified"] = user.verified ? "true" : "false";
  csv["_ref_chat_only"] = user.chat_only ? "true" : "false";
  csv["_ref_two_factor_auth_enabled"] = user.two_factor_auth_enabled
    ? "true"
    : "false";
  csv["_ref_shared"] = user.shared ? "true" : "false";
  csv["_ref_shared_agent"] = user.shared_agent ? "true" : "false";
  csv["_ref_shared_phone_number"] = user.shared_phone_number
    ? "true"
    : "false";
  csv["_ref_last_login_at"] = user.last_login_at ?? "";
  csv["_ref_created_at"] = user.created_at ?? "";
  csv["_ref_updated_at"] = user.updated_at ?? "";
  csv["_ref_report_csv"] = user.report_csv ? "true" : "false";

  return csv;
};

export const buildCustomerCsvHeaders = (
  activeFields: ICustomerField[],
): string[] => {
  const fixedHeaders = [
    "name",
    "emails",
    "ticket_restriction",
    "external_id",
    "details",
    "notes",
    "phones",
    "shared_phone",
    "role",
    "custom_role",
    "alias",
    "time_zone",
    "locale",
    "locale_id",
    "moderator",
    "only_private_comments",
    "remote_photo_url",
    "signature",
    "suspended",
    "skip_verify_email",
    "organizations",
    "default_group_id",
    "tags",
  ];

  const customHeaders = activeFields.map((f) => `custom_fields.${f.key}`);

  const refHeaders = [
    "_ref_id",
    "_ref_url",
    "_ref_role_type",
    "_ref_restricted_agent",
    "_ref_iana_time_zone",
    "_ref_active",
    "_ref_verified",
    "_ref_chat_only",
    "_ref_two_factor_auth_enabled",
    "_ref_shared",
    "_ref_shared_agent",
    "_ref_shared_phone_number",
    "_ref_last_login_at",
    "_ref_created_at",
    "_ref_updated_at",
    "_ref_report_csv",
  ];

  return [...fixedHeaders, ...customHeaders, ...refHeaders];
};

export const buildOrgMap = (
  orgs: { id: number; name: string }[],
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const org of orgs) {
    map.set(org.id, org.name);
  }
  return map;
};

export const buildCustomRoleMap = (
  roles: { id: number; name: string }[],
): Map<number, string> => {
  const map = new Map<number, string>();
  for (const role of roles) {
    map.set(role.id, role.name);
  }
  return map;
};
